import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { LinkedinIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface TeamMember {
  id: string;
  name: string;
  position: string;
  bio: string;
  photo_url: string | null;
  linkedin_url: string | null;
  slug: string;
  sort_order: number;
  specialties: { specialty_name: string }[];
  qualifications: { qualification_name: string }[];
}

const Team = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('id, name, position, bio, photo_url, linkedin_url, slug, sort_order')
        .eq('status', 'active')
        .order('sort_order');
      
      console.log('Team fetch result:', { data, error });
      
      if (error) {
        console.error('Error fetching team members:', error);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const memberIds = data.map(m => m.id);
        const [specsResult, qualsResult] = await Promise.all([
          supabase.from('team_member_specialties').select('team_member_id, specialty_name').in('team_member_id', memberIds),
          supabase.from('team_member_qualifications').select('team_member_id, qualification_name').in('team_member_id', memberIds),
        ]);

        setTeamMembers(data.map(m => ({
          ...m,
          specialties: (specsResult.data || []).filter(s => s.team_member_id === m.id),
          qualifications: (qualsResult.data || []).filter(q => q.team_member_id === m.id),
        })));
      }
      setLoading(false);
    };
    fetchTeam();
  }, []);

  return (
    <div className="py-8">
      <SEO
        title="Our Team — Investment Professionals at CBAM"
        description="Meet the experienced investment professionals behind Cape Berkshire Asset Management — portfolio managers, analysts and advisers serving UK financial advisers."
        path="/team"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Team</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              Meet our experienced team of investment professionals dedicated to managing 
              your wealth with expertise, integrity, and personalised attention.
            </p>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2].map(i => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-square w-full" />
                  <CardHeader><Skeleton className="h-6 w-40" /><Skeleton className="h-4 w-24 mt-2" /></CardHeader>
                  <CardContent><Skeleton className="h-16 w-full" /></CardContent>
                </Card>
              ))}
            </div>
          ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${teamMembers.length >= 5 ? "lg:grid-cols-3 max-w-7xl" : "max-w-4xl"} mx-auto`}>
            {teamMembers.map((member) => (
              <Card key={member.id} className="overflow-hidden">
                <div className="aspect-square bg-muted relative">
                  {member.photo_url ? (
                    <img 
                      src={member.photo_url} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                      <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-primary">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <CardTitle className="text-xl">{member.name}</CardTitle>
                      <CardDescription className="text-primary font-medium">
                        {member.position}
                      </CardDescription>
                    </div>
                  </div>
                  {member.qualifications.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {member.qualifications.map((q, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{q.qualification_name}</Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {member.bio}
                  </p>
                  {member.specialties.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm mb-2">Specialties:</h4>
                      <div className="flex flex-wrap gap-1">
                        {member.specialties.map((specialty, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {specialty.specialty_name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 mb-4">
                    {member.linkedin_url && (
                      <Button size="sm" variant="outline" className="flex-1" asChild>
                        <a 
                          href={member.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedinIcon className="h-4 w-4 mr-1" />
                          LinkedIn
                        </a>
                      </Button>
                    )}
                  </div>
                  {/* Every active member links to a profile: mark-insley and sam-hallett have
                      bespoke pages, everyone else renders from their team_members row via the
                      /team/:slug route. */}
                  <Button size="sm" className="w-full" asChild>
                    <Link to={`/team/${member.slug}`}>
                      View Full Profile
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Team Values */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Values</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do for our clients.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Our team combines decades of investment experience with continuous education 
                  to stay at the forefront of portfolio management.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Integrity</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We operate with complete transparency and always act in our clients' best interests, 
                  maintaining the highest ethical standards.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Innovation</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We leverage cutting-edge technology and research methodologies to deliver 
                  superior investment outcomes for our clients.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Work With Our Expert Team
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Ready to discuss your investment goals with our experienced professionals? 
            Book a consultation today.
          </p>
          <Button size="lg" asChild>
            <Link to="/contact">Book Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Team;